import {
    DOSSIERS_OPEN_SEARCH_SLICE_NAME,
    useFormaliteService,
    useUtilisateurService,
} from '@europrocurement/l2d-domain';
import { type DossierOpenSearchJsonldOpensearchDossierStatutRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { Accordion, ReactGridContainer } from '@europrocurement/flexy-components';
import { dateToISOYmd, stringToHexColor } from '@europrocurement/l2d-utils';
import { type NavigateFunction } from 'react-router';
import { useDispatch } from 'react-redux';
import {
    type GroupesDeTravailJsonldUtilisateurReadDomaineRead,
    type UtilisateursJsonldUserworkgroupRead,
} from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import ChartJsBar, {
    type ChartJsBarDataType,
} from '@europrocurement/flexy-components/components/molecules/ChartJsBar/ChartJsBar';
import FiltersDashboard from '../../components/FiltersDashboard/FiltersDashboard';
import DossierStatusWidgetDashboard from '../../components/DossierStatusWidgetDashboard/DossierStatusWidgetDashboard';

type FormalistDashboardProps = {
    title: string;
    formalistId: number;
    listeDossiersPath: string;
    navigate: NavigateFunction;
};

export type DashboardFiltersType = {
    startedAt: string;
    endedAt?: string;
};

const FormalistManagerDashboard: React.FunctionComponent<FormalistDashboardProps> = function ({
    title,
    formalistId,
    listeDossiersPath,
    navigate,
}) {
    const { palette } = useTheme();
    const { dossierOpenSearchApi } = useFormaliteService();
    const { groupesTravailApi, utilisateursGroupeTravailApi } = useUtilisateurService();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    const [statusDossierOpenSearch, setStatusDossierOpenSearch] =
        useState<DossierOpenSearchJsonldOpensearchDossierStatutRead[]>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [userGroups, setUserGroups] = useState<
        GroupesDeTravailJsonldUtilisateurReadDomaineRead[]
    >([]);
    const [usersInGroups, setUsersInGroups] = useState<UtilisateursJsonldUserworkgroupRead[]>([]);
    const [chartData, setChartData] = useState<ChartJsBarDataType>();

    // Filters
    const initialStartDate = useMemo(() => {
        const DAYS_OFFSET = 30;
        const date = new Date();
        date.setDate(date.getDate() - DAYS_OFFSET);
        return dateToISOYmd(date);
    }, []);
    const [filters, setFilters] = useState<DashboardFiltersType>({
        startedAt: initialStartDate,
        endedAt: undefined,
    });

    // Retriveve current user referent groups
    useEffect(() => {
        if (formalistId) {
            groupesTravailApi
                .apiGroupestravailGetCollection({
                    iduserReferent: formalistId.toString(),
                })
                .then((res) => {
                    setUserGroups(res.data['hydra:member']);
                })
                .catch((err) => {
                    console.log(err);
                    enqueueSnackbar(
                        'Une erreur est survenue lors de la récupération de vos groupes',
                        {
                            variant: 'error',
                        },
                    );
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formalistId]);

    // retrieve users linked to the user referent groups
    useEffect(() => {
        if (userGroups.length > 0) {
            utilisateursGroupeTravailApi
                .apiUtilisateurgroupetravailGetCollection({
                    groupetravail2: userGroups.map((userGroup) => userGroup['@id'] as string),
                })
                .then((res) => {
                    setUsersInGroups(
                        res.data['hydra:member']
                            .filter((userGrp) => userGrp.user!.id !== formalistId)
                            .map((userGrp) => userGrp.user!),
                    );
                })
                .catch((err) => {
                    console.log(err);
                    enqueueSnackbar(
                        'Une erreur est survenue lors de la récupération des utilisateurs de vos groupes',
                        {
                            variant: 'error',
                        },
                    );
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userGroups]);

    useEffect(() => {
        dossierOpenSearchApi
            .apiOpensearchFormalistesGetCollection({
                formalisteId: [...usersInGroups.map((user) => user.id as number), formalistId],
                startDate: filters.startedAt,
                endDate: filters.endedAt,
            })
            .then((res) => {
                const newChartData: ChartJsBarDataType = {
                    labels: [],
                    datasets: [
                        {
                            label: 'Nombre de dossiers',
                            data: [],
                            backgroundColor: [],
                            borderWidth: 1,
                        },
                    ],
                };

                res.data['hydra:member'].map((dossFormalist) => {
                    newChartData.labels.push(dossFormalist.nomComplet as string);
                    newChartData.datasets[0].data.push(dossFormalist.childsCount as number);
                    newChartData.datasets[0].backgroundColor.push(
                        stringToHexColor(
                            ((dossFormalist.nomComplet as string) + dossFormalist.childsCount ||
                                0) as string,
                        ),
                    );
                    return true;
                });
                setChartData(newChartData);
            })
            .catch((err) => {
                console.log(err);
                enqueueSnackbar(
                    'Une erreur est survenue lors de la récupération des dossiers par utilisateurs',
                    {
                        variant: 'error',
                    },
                );
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usersInGroups, filters]);

    const handleDossierStatusClick = (statusId?: number | null) => {
        if (!statusId) {
            return;
        }
        // dispatch  dossier status to redux and open the dossier list
        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainPreFilter`,
            payload: {
                filters: [
                    {
                        field: 'statutId',
                        value: statusId,
                    },
                ],
            },
        });

        navigate(`/${listeDossiersPath}`);
    };

    const fetchDossierStatus = async () => {
        setIsSubmitting(true);
        const dossierStatusResponse = await dossierOpenSearchApi.apiOpensearchStatusesGetCollection(
            {
                formalisteId: [formalistId, ...usersInGroups.map((user) => user.id as number)],
                itemsPerPage: 20,
                startDate: filters.startedAt,
                endDate: filters.endedAt,
            },
        );

        setIsSubmitting(false);
        if (dossierStatusResponse.status === 200) {
            return dossierStatusResponse.data['hydra:member'];
        }
        enqueueSnackbar('Une erreur est survenue lors de la récupération des dossiers', {
            variant: 'error',
        });
        return [];
    };

    // refetch dossierStatus on filter or formalistId update
    useEffect(() => {
        fetchDossierStatus().then((res) => setStatusDossierOpenSearch(res));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formalistId, usersInGroups, filters.startedAt, filters.endedAt]);

    return (
        <Accordion
            title={title}
            expandLabel={
                <FiltersDashboard
                    isSubmitting={isSubmitting}
                    setFilters={setFilters}
                    initialStartDate={initialStartDate}
                />
            }
            expanded
            expandColor={palette.primary}
            sx={{ boxShadow: 'unset', border: 'none' }}
            contentSxProps={{ gap: '16px', display: 'grid' }}
        >
            {statusDossierOpenSearch && (
                <DossierStatusWidgetDashboard
                    statusDossierOpenSearch={statusDossierOpenSearch}
                    handleDossierStatusClick={handleDossierStatusClick}
                />
            )}

            <ReactGridContainer
                localStorageName="grid-formalist-dossier-formalist-manager"
                gridconfig={{ cols: 6, rowHeight: 30 }}
                layoutProps={[
                    {
                        i: 'chartJs-bar-formalist',
                        x: 0,
                        y: 0,
                        w: 4,
                        h: 8,
                        isResizable: true,
                        resizeHandles: ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'],
                    },
                ]}
            >
                <Box key="chartJs-bar-formalist">
                    {chartData && (
                        <ChartJsBar
                            direction="y"
                            displayLegend={false}
                            displayTitle
                            title="Nombre de dossiers par formaliste"
                            data={chartData}
                        />
                    )}
                </Box>
            </ReactGridContainer>
        </Accordion>
    );
};

export default FormalistManagerDashboard;
